/**
 * UFC Fight Props rows (#848) — the ONE description of how a Kalshi (or,
 * below, Polymarket) fight-prop market becomes a tradable synthetic GameRow, shared by the table
 * (GameLinesSection) and the parent's live-ticket resolver
 * (GameDetail/index.tsx `livePanelGame`).
 *
 * Same contract as the football Game Props rows (cfbEventRows): one Kalshi
 * book in both slots of a singleSide row; the away slot carries the YES
 * proposition, the home slot its complete negation, which the order ticket
 * reads as its YES/NO wording ("Fight ends before round 3 — YES"). The synth
 * key carries the exact Kalshi ticker; the live resolver re-finds that ticker
 * in the CURRENT selector result on every frame and fails closed (null) when
 * the bout, the selector result or the ticker is gone — a click-time snapshot
 * is never revived.
 */
import {
  UFC_FIGHT_PROP_SYNTH_MARKER,
  cfbEventNoLabel,
  ufcFightPropSynthKey,
} from '@/lib/sport/cfbEventSynthKey';
import { ufcFightPropLabel } from '@/lib/ufc/fightProps';
import type { GameRow, PolyFightPropRow, UfcFightPropRow, UfcFightProps } from '@/views/ValueDashboard/types';
import { buildCfbEventSynth } from './cfbEventRows';

export { ufcFightPropSynthKey };

/** The fighter's display name on this game row. */
function fighterName(game: GameRow, side: 'away' | 'home' | null, code: string): string {
  if (side === 'away') return game.awayName || game.awayCode || code;
  if (side === 'home') return game.homeName || game.homeCode || code;
  return code;
}

/** The row's YES proposition (and the ticket's YES wording). */
export function ufcFightPropRowLabel(game: GameRow, row: UfcFightPropRow): string {
  return ufcFightPropLabel(row.leg, (code) => fighterName(game, row.fighterSide, code));
}

export function buildUfcFightPropSynth(game: GameRow, row: UfcFightPropRow): GameRow {
  const yes = ufcFightPropRowLabel(game, row);
  return buildCfbEventSynth(game, ufcFightPropSynthKey(game.key, row.ticker), yes, cfbEventNoLabel(yes), row.kalshi);
}

/**
 * A Polymarket fight prop row (carried from node10): the same single-binary
 * synth, with the one Poly book in both Poly slots and no Kalshi side. The
 * synth key carries the exact Poly ticker under the same marker, so the
 * ticket reads its YES/NO wording from the two name slots exactly as it does
 * for a Kalshi prop. A rounds total's YES is the Over.
 */
export function polyFightPropYesLabel(row: PolyFightPropRow): string {
  return row.group === 'rounds' && row.line != null ? `Over ${row.line} rounds` : row.title;
}

export function buildPolyFightPropSynth(game: GameRow, row: PolyFightPropRow): GameRow {
  const yes = polyFightPropYesLabel(row);
  const no = row.group === 'rounds' && row.line != null ? `Under ${row.line} rounds` : cfbEventNoLabel(yes);
  return {
    ...game,
    key: ufcFightPropSynthKey(game.key, row.ticker),
    gameState: 'pre',
    finalWinnerSide: null,
    awayName: yes,
    homeName: no,
    kalshi: null,
    polymarket: { away: row.polymarket, home: row.polymarket },
  };
}

export function isUfcFightPropSynthKey(gameKey: string, synthKey: string): boolean {
  return synthKey.startsWith(`${gameKey}${UFC_FIGHT_PROP_SYNTH_MARKER}`);
}

/** Re-resolve a fight-prop synth from the live selector result. */
export function findLiveUfcFightPropSynth(
  game: GameRow,
  synthKey: string,
  props: UfcFightProps | null,
  polyProps: readonly PolyFightPropRow[] = [],
): GameRow | null {
  if (!isUfcFightPropSynthKey(game.key, synthKey)) return null;
  const ticker = synthKey.slice(game.key.length + UFC_FIGHT_PROP_SYNTH_MARKER.length);
  for (const g of props?.groups ?? []) {
    const row = g.rows.find((r) => r.ticker === ticker);
    if (row) return buildUfcFightPropSynth(game, row);
  }
  const polyRow = polyProps.find((r) => r.ticker === ticker);
  return polyRow ? buildPolyFightPropSynth(game, polyRow) : null;
}

/** Parent-panel boundary. `undefined` = not a fight-prop synth (continue to
 *  the other resolvers); `null` = a fight-prop synth whose base game, selector
 *  result or exact ticker is no longer current — the ticket fails closed. */
export function resolveLiveUfcFightPropPanelGame(
  game: GameRow | null,
  synthKey: string,
  getFightProps: (gameKey: string) => UfcFightProps | null,
  getPolyFightProps: (polyTicker: string) => readonly PolyFightPropRow[] = () => [],
): GameRow | null | undefined {
  const marker = synthKey.indexOf(UFC_FIGHT_PROP_SYNTH_MARKER);
  if (marker < 0) return undefined;
  const baseGameKey = synthKey.slice(0, marker);
  if (!game || game.key !== baseGameKey) return null;
  // Poly props re-resolve off the live row's own Poly moneyline ticker.
  const polyTicker = game.polymarket?.away.marketTicker ?? null;
  return findLiveUfcFightPropSynth(
    game,
    synthKey,
    getFightProps(game.key),
    polyTicker ? getPolyFightProps(polyTicker) : [],
  );
}
